import { VirtualHostInfo } from "../InfoSets/VirtualHostInfo";
import { List } from "../Utils/List";
import { Dictionary } from "../Utils/Dictionary";
import { ObjectSerializer } from "../Serialization/ObjectSerializer";
import { ObjSerUtils } from "../Serialization/ObjSerUtils";
import { ValueEncoder } from "../Utils/ValueEncoder";

export class VirtualSwitch implements ObjectSerializer {

	public Key: string;

	public HostID: string;
	public get HasHostID(): boolean {
		return !!this.HostID;
	}

	public Name: string;
	public get HasName(): boolean {
		return !!this.Name;
	}

	public NumPorts: number;
	public NumPortsAvailable: number;

	public PhysicalNicKeys: List<string>;
	public get HasPhysicalNicKeys(): boolean {
		return this.PhysicalNicKeys && this.PhysicalNicKeys.Count > 0;
	}
		
	public PortGroupKeys: List<string>;
	public get HasPortGroupKeys(): boolean {
		return this.PortGroupKeys && this.PortGroupKeys.Count > 0;
	}

	// DVS - Distributed Virtual Switch
	public DvsName: string;
	public get HasDvsName(): boolean {
		return !!this.DvsName;
	}

	public DvsUUID: string;
	public get HasDvsUUID(): boolean {
		return !!this.DvsUUID;
	}

	public ConfigNumPorts: number;
	public get HasConfigNumPorts(): boolean {
		return (this.ConfigNumPorts >= 0);
	}

	constructor(key?: string) {
		this.Key = key;
		this.Init();
	}

	private Init():void {
		this.HostID = '';
		this.Name = '';

		this.NumPorts = 0;
		this.NumPortsAvailable = 0;

		if (this.PhysicalNicKeys == null)
			this.PhysicalNicKeys = new List<string>();
		else
			this.PhysicalNicKeys.Clear();

		if (this.PortGroupKeys == null)
			this.PortGroupKeys = new List<string>();
		else
			this.PortGroupKeys.Clear();

		this.DvsName = '';
		this.DvsUUID = '';
		this.ConfigNumPorts = -1;
	}

	public Write(obj: any, context: any): void {
		obj.virtual_switch_entry = {};
		obj = obj.virtual_switch_entry;

		obj.key = this.Key;

		if (this.HasHostID)
			obj.host_id = this.HostID;

		if (this.HasName)
			obj.name = this.Name;

		if (this.NumPorts >= 0)
			obj.num_ports = ValueEncoder.EncodeInteger(this.NumPorts);

		if (this.NumPortsAvailable >= 0)
			obj.num_ports_available = ValueEncoder.EncodeInteger(this.NumPortsAvailable);

		if (this.HasPhysicalNicKeys) {
			obj.physical_nic_key_table = [];
			ObjSerUtils.WriteCollection(obj.physical_nic_key_table, this.PhysicalNicKeys, (value: string) => {
				return { list_value: value };
			}, context);
		}

		if (this.HasPortGroupKeys) {
			obj.port_group_key_table = [];
			ObjSerUtils.WriteCollection(obj.port_group_key_table, this.PortGroupKeys, (value: string) => {
				return { list_value: value };
			}, context);
		}

		// DVS - Distributed Virtual Switch
		if (this.HasDvsName)
			obj.dvs_name = this.DvsName;
		if (this.HasDvsUUID)
			obj.dvs_uuid = this.DvsUUID;
		if (this.HasConfigNumPorts)
			obj.cfg_num_ports = ValueEncoder.EncodeInteger(this.ConfigNumPorts);
	}

	public Read(obj: any, context: any): void {
		if (!obj.virtual_switch_entry)
			throw new Error();
		obj = obj.virtual_switch_entry;

		if (obj.key !== undefined)
			this.Key = obj.key;

		if (obj.host_id !== undefined)
			this.HostID = obj.host_id;

		if (obj.name !== undefined)
			this.Name = obj.name;

		if (obj.num_ports !== undefined)
			this.NumPorts = ValueEncoder.DecodeInteger(obj.num_ports);

		if (obj.num_ports_available !== undefined)
			this.NumPortsAvailable = ValueEncoder.DecodeInteger(obj.num_ports_available);

		if (obj.physical_nic_key_table !== undefined) {
			ObjSerUtils.ReadCollection(obj.physical_nic_key_table, this.PhysicalNicKeys, (elem: any) => {
				return elem.list_value;
			}, context);
		}

		if (obj.port_group_key_table !== undefined) {
			ObjSerUtils.ReadCollection(obj.port_group_key_table, this.PortGroupKeys, (elem: any) => {
				return elem.list_value;
			}, context);
		}

		if (obj.dvs_name !== undefined)
			this.DvsName = obj.dvs_name;

		if (obj.dvs_uuid !== undefined)
			this.DvsUUID = obj.dvs_uuid;

		if (obj.cfg_num_ports !== undefined)
			this.ConfigNumPorts = ValueEncoder.DecodeInteger(obj.cfg_num_ports);
	}
}

export class VirtualSwitchTable implements ObjectSerializer {

	private _switchTable: Dictionary<string, VirtualSwitch>;

	public VirtualHost: VirtualHostInfo;
	public get HasVirtualHost(): boolean {
		return this.VirtualHost && this.VirtualHost.HasHostID;
	}

	constructor(virtualHost: VirtualHostInfo) {
		this._switchTable = new Dictionary<string, VirtualSwitch>();
	}

	public Add(switchKey: string): VirtualSwitch {
		var hvSwitch: VirtualSwitch = null;

		if (this._switchTable.ContainsKey(switchKey)) {
			hvSwitch = this._switchTable.Item(switchKey);
		}
		else {
			hvSwitch = new VirtualSwitch(switchKey);
			this._switchTable.Add(switchKey, hvSwitch);
		}

		if (this.HasVirtualHost)
			hvSwitch.HostID = this.VirtualHost.HostID;

		return hvSwitch;
	}

	public AddSwitch(vSwitch: VirtualSwitch):void {
		this._switchTable.Remove(vSwitch.Key);

		this._switchTable.Add(vSwitch.Key, vSwitch);

		if (this.HasVirtualHost)
			vSwitch.HostID = this.VirtualHost.HostID;
	}

	public Clear(): void {
		this._switchTable.Clear();
	}

	public get Count():number {
		return this._switchTable.Count;
	}

	public Remove(switchKey: string):boolean {
		return this._switchTable.Remove(switchKey);
	}

	public Write(obj: any, context: any): void {
		obj.virtual_switch_table = {};
		obj = obj.virtual_switch_table;

		obj.entries = [];
		ObjSerUtils.WriteObjSerCollection(obj.entries, this._switchTable.Values(), context);
	}

	public Read(obj: any, context: any): void {
		if (!obj.virtual_switch_table)
			throw new Error();
		obj = obj.virtual_switch_table;

		this.Clear();

		if (obj.entries !== undefined) {
			obj.entries.forEach((value: any) => {
				let entry = new VirtualSwitch();
				entry.Read(value, context);
				this.AddSwitch(entry);
			}, this);
		}
	}
}
